import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import type { AuthUser } from "@/contexts/AuthContext";
import { useSupportSocket } from "@/hooks/useSupportSocket";
import { getCurrentSupportConversation } from "@/services/supportChatService";
import { subscribeOpenSupportChat } from "@/components/support/supportChatControls";
import { playSupportChime } from "@/lib/supportChime";

// Lazy so the chat panel (and its socket hook) stay out of the initial
// bundle — the bubble is on every authenticated page, the panel is not.
const SupportChatPanel = lazy(() =>
  import("@/components/support/SupportChatPanel").then((m) => ({
    default: m.SupportChatPanel,
  }))
);

/**
 * No bubble on sign-in / sign-up / recovery / invite flows (incl.
 * magic-link verify). Originally ported from LiveChatBridge and since
 * diverged — the /kyc-callback entry went with the Didit removal. These are
 * pages where a support bubble is noise.
 */
function shouldHideSupportOnPath(pathname: string): boolean {
  if (pathname === "/login" || pathname === "/register") {
    return true;
  }
  if (
    pathname === "/auth/forgot-password" ||
    pathname === "/auth/reset-password" ||
    pathname === "/auth/verify" ||
    pathname === "/auth/sso/callback"
  ) {
    return true;
  }
  if (pathname.startsWith("/accept-invitation")) {
    return true;
  }
  return false;
}

/** Operators answer from /platform-support; they don't need the customer bubble. */
function isPlatformOperator(user: AuthUser): boolean {
  return user.platformRole === "root" || user.platformRole === "root_admin";
}

export function SupportChatBridge(): JSX.Element | null {
  const { user, isLoading } = useAuth();
  const { pathname } = useLocation();
  /**
   * Open on arrival from a "respondimos tu consulta" email.
   *
   * Those mails link to /?supportConversationId=<id>. Landing on the
   * dashboard with the bubble shut is the same dead end as no link at
   * all — the customer came to read a reply and has to go find it.
   * Read once at mount so closing the panel actually closes it.
   */
  /**
   * Kept mounted while it plays its exit animation. Unmounting on the
   * click is what made closing feel abrupt beside an animated open —
   * there is nothing left on screen to animate.
   */
  const [closing, setClosing] = useState(false);
  const [open, setOpen] = useState(
    () =>
      new URLSearchParams(window.location.search).has("supportConversationId")
  );
  const [unread, setUnread] = useState(0);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const openRef = useRef(false);
  openRef.current = open;

  // The bridge holds its own socket so a reply can be announced while the
  // panel is CLOSED — the panel's socket only exists while it is mounted,
  // which is precisely when the badge is not needed.
  useEffect(() => {
    if (isLoading || !user) return;
    let cancelled = false;
    // LOOK, do not create. This ran on every authenticated page load, so
    // the create-on-read version minted a conversation for anyone who
    // merely visited the portal — nine of them in production, all empty.
    void getCurrentSupportConversation()
      .then((c) => {
        if (!cancelled && c) setConversationId(c.id);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isLoading, user]);

  const onMessage = useCallback(
    (message: { authorKind: string }) => {
      if (message.authorKind === "customer") return;
      if (openRef.current) return;
      setUnread((n) => n + 1);
      playSupportChime();
    },
    []
  );

  /**
   * Close in two steps: play the exit, THEN unmount. The timeout matches
   * the 150ms of .fx-chat-panel-out — a shorter one cuts the animation
   * off mid-frame, a longer one leaves an invisible panel holding the
   * layout.
   */
  /**
   * What the BUBBLE should look like. Not `open`, which stays true for
   * the 150ms the panel takes to leave — driving the icon off it made
   * the X snap back to the chat glyph after the panel had already gone,
   * and made closing from the header feel different from closing from
   * the bubble even though both run the same code.
   */
  const showingPanel = open && !closing;

  const closePanel = useCallback((): void => {
    setClosing(true);
    window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 150);
  }, []);

  // Let any component open the panel programmatically (e.g. the onboarding
  // "homologación pendiente" screen's "¿Dudas mientras tanto?" card). Mirrors
  // the bubble's own open path: clear unread + cancel any in-flight close.
  useEffect(() => {
    return subscribeOpenSupportChat(() => {
      setClosing(false);
      setUnread(0);
      setOpen(true);
    });
  }, []);

  useSupportSocket(conversationId, { onMessage });

  // Path check first, before isLoading — otherwise the bubble flashes on
  // /login while the session is being restored.
  if (shouldHideSupportOnPath(pathname)) {
    return null;
  }
  if (isLoading || !user) {
    return null;
  }
  if (isPlatformOperator(user)) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3">
      {open ? (
        <Suspense fallback={null}>
          <SupportChatPanel closing={closing} onClose={closePanel} />
        </Suspense>
      ) : null}
      <button
        type="button"
        onClick={() => {
          // Guard on showingPanel, not open: a click landing during the
          // exit animation would otherwise start a second close and
          // leave the panel unmounted with `closing` stuck true.
          if (showingPanel) {
            closePanel();
            return;
          }
          setUnread(0);
          setOpen(true);
        }}
        aria-label={unread > 0 ? `Soporte (${unread} sin leer)` : "Soporte"}
        aria-expanded={showingPanel}
        className={[
          "fx-chat-bubble relative flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-accent text-accent-foreground shadow-xl ring-2 ring-background/70 hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          // Ambient halo only while someone is actually waiting on us.
          // Always-on motion on a fixed element is a distraction the user
          // cannot dismiss; tied to unread, it is a notification.
          unread > 0 && !showingPanel ? "fx-chat-halo" : "",
        ].join(" ")}
      >
        {/*
          The icon turns into the close affordance rather than being
          swapped abruptly — the button keeps its identity across the
          state change instead of appearing to become a different control.
        */}
        <MessageCircle
          className={[
            "h-6 w-6 transition-transform duration-200 ease-out",
            showingPanel ? "rotate-90 scale-90 opacity-0" : "rotate-0 scale-100",
          ].join(" ")}
        />
        <X
          aria-hidden="true"
          className={[
            "absolute h-6 w-6 transition-transform duration-200 ease-out",
            showingPanel
              ? "rotate-0 scale-100 opacity-100"
              : "-rotate-90 scale-90 opacity-0",
          ].join(" ")}
        />
        {unread > 0 && !showingPanel ? (
          <span
            aria-hidden="true"
            className="fx-chat-badge absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold leading-none text-destructive-foreground ring-2 ring-background"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </button>
    </div>
  );
}
